import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { TimelineComment } from "@/types/video-validation";

export type VideoComment = Tables<"video_comments">;
export type VideoCommentInsert = TablesInsert<"video_comments">;
export type VideoCommentUpdate = TablesUpdate<"video_comments">;

/**
 * Create a single video comment immediately (as draft, without batch_id)
 */
export const createVideoComment = async (comment: VideoCommentInsert) => {
  try {
    const { data, error } = await supabase
      .from("video_comments")
      .insert(comment)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error creating video comment:", error);
    return { success: false, error };
  }
};

export const getVideoComments = async (videoContentId: string, videoVersion?: number) => {
  try {
    let query = supabase
      .from("video_comments")
      .select("*, users(first_name, last_name, avatar_url)")
      .eq("video_content_id", videoContentId)
      .order("created_at", { ascending: true });

    if (videoVersion) {
      query = query.eq("video_version", videoVersion);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching video comments:", error);
    return { success: false, error };
  }
};

export const updateVideoComment = async (id: string, updates: VideoCommentUpdate) => {
  try {
    const { data, error } = await supabase
      .from("video_comments")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error updating video comment:", error);
    return { success: false, error };
  }
};

export const deleteVideoComment = async (id: string) => {
  try {
    const { error } = await supabase
      .from("video_comments")
      .delete()
      .eq("id", id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting video comment:", error);
    return { success: false, error };
  }
};

/**
 * Get all comments for a video with batch, user and asset info
 */
export const getVideoCommentsWithBatches = async (videoContentId: string, videoVersion?: number) => {
  try {
    let query = supabase
      .from("video_comments")
      .select(`
        *,
        users(first_name, last_name, avatar_url),
        video_comment_batch(id, sent_at),
        user_assets(id, file_name, file_path, file_type)
      `)
      .eq("video_content_id", videoContentId)
      .order("timestamp_seconds", { ascending: true });

    if (videoVersion) {
      query = query.eq("video_version", videoVersion);
    }

    const { data, error } = await query;

    if (error) throw error;

    // If the bucket is private, public URLs will look valid but won't render.
    // We precompute signed URLs for attached assets.
    if (data?.length) {
      await Promise.all(
        data.map(async (comment: any) => {
          const filePath = comment?.user_assets?.file_path;
          if (!filePath) return;

          const { data: signedData, error: signedError } = await supabase.storage
            .from('user-assets')
            .createSignedUrl(filePath, 60 * 60);

          if (!signedError && signedData?.signedUrl && comment.user_assets) {
            comment.user_assets.signed_url = signedData.signedUrl;
          }
        })
      );
    }

    return { success: true, data };
  } catch (error) {
    console.error("Error fetching video comments with batches:", error);
    return { success: false, error };
  }
};

/**
 * Link existing draft comments to a batch (set batch_id on existing comments)
 */
export const linkCommentsToBatch = async (commentIds: string[], batchId: string) => {
  try {
    const { data, error } = await supabase
      .from("video_comments")
      .update({ batch_id: batchId })
      .in("id", commentIds)
      .select();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error("Error linking comments to batch:", error);
    return { success: false, error };
  }
};

/**
 * Delete a draft comment (only comments without batch_id)
 */
export const deleteDraftComment = async (commentId: string) => {
  try {
    const { error } = await supabase
      .from("video_comments")
      .delete()
      .eq("id", commentId)
      .is("batch_id", null);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting draft comment:", error);
    return { success: false, error };
  }
};

/**
 * Convert database comment to TimelineComment
 */
export const mapDbCommentToTimelineComment = (
  dbComment: VideoComment & { 
    users?: { first_name: string | null; last_name: string | null; avatar_url: string | null } | null;
    video_comment_batch?: { id: string; sent_at: string } | null;
    user_assets?: { id: string; file_name: string; file_path: string; file_type: string | null; signed_url?: string } | null;
  }
): TimelineComment => {
  const userName = dbComment.users
    ? `${dbComment.users.first_name || ''} ${dbComment.users.last_name || ''}`.trim() || 'Unknown'
    : 'Unknown';

  // Prefer signed URL (works for private buckets). Fallback to public URL.
  let assetUrl: string | undefined = dbComment.user_assets?.signed_url;
  if (!assetUrl && dbComment.user_assets?.file_path) {
    const { data } = supabase.storage
      .from('user-assets')
      .getPublicUrl(dbComment.user_assets.file_path);
    assetUrl = data.publicUrl;
  }

  return {
    id: dbComment.id,
    dbId: dbComment.id,
    timestamp: dbComment.timestamp_seconds || 0,
    text: dbComment.comment_text,
    author: userName,
    authorAvatar: dbComment.users?.avatar_url || undefined,
    createdAt: new Date(dbComment.created_at),
    parentId: dbComment.parent_comment_id || undefined,
    batchId: dbComment.batch_id || undefined,
    isSent: !!dbComment.batch_id,
    userId: dbComment.user_id,
    assetId: dbComment.asset_id || undefined,
    assetUrl,
    assetFileName: dbComment.user_assets?.file_name || undefined,
    assetFileType: dbComment.user_assets?.file_type || undefined,
  };
};
